import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
    AirspaceAuthorisationDto,
    AirspaceAuthorisationResponse,
    ApproversMissionApprovalDto
} from '@flyfreely-portal-ui/flyfreely';
import { AirspaceAuthorisationState } from '../../interfaces';
import { AirspaceAuthorisationForm } from '../airspace-authorisation-preview.service';

/**
 * Discriminated union of every mode the dialogue footer can be in.
 * Computed in `ngOnChanges` from the incoming `@Input()` values so the
 * template has a single clean switch rather than six overlapping `*ngIf`
 * chains.
 */
export type FooterMode =
    /** Preview is available; user can review and submit */
    | 'NEW_REQUEST'
    /** Form is ready but no preview could be generated */
    | 'NO_PREVIEW'
    /** An active / pending authorisation is shown */
    | 'ACTIVE'
    /** Authorisation is completed or closed */
    | 'COMPLETED'
    /** Authorisation was cancelled */
    | 'CANCELLED'
    /** Authorisation was rejected */
    | 'REJECTED'
    /** Service is unavailable or an operation failed — footer suppressed */
    | 'HIDDEN';

@Component({
    selector: 'authorisation-footer',
    templateUrl: './authorisation-footer.component.html'
})
export class AuthorisationFooterComponent implements OnChanges {
    @Input() authorisations: AirspaceAuthorisationDto[];
    @Input() requestNew: boolean;
    @Input() authorisationPreview: AirspaceAuthorisationResponse | null;
    @Input() authorisationForm: FormGroup<AirspaceAuthorisationForm> | null;
    @Input() additionalInformationFormValid: boolean = true;
    @Input()
    airspaceAuthorisationType: AirspaceAuthorisationDto.AuthorisationType;
    @Input() airspaceAuthorisationStatus: AirspaceAuthorisationState;
    @Input() errorMessage: string | null;
    @Input() canEditMission: boolean;
    @Input() approval: ApproversMissionApprovalDto | null;
    @Input() operationFailed: boolean;
    @Input() authorisationServiceUnavailable: boolean;
    @Input() working: boolean;
    @Input() editStart: boolean;
    @Input() editEnd: boolean;
    @Input() approvedIds: string[] = [];

    @Output() requestAuthorisation = new EventEmitter<void>();
    @Output() showMissionEditDialogue = new EventEmitter<void>();
    @Output() close = new EventEmitter<void>();

    mode: FooterMode = 'HIDDEN';

    get approvedId(): string {
        return this.approvedIds?.[0] ?? '';
    }

    ngOnChanges() {
        this.mode = this.computeMode();
    }

    private computeMode(): FooterMode {
        const hasAuthorisations =
            this.authorisations != null && this.authorisations.length > 0;
        const isNewRequest = !hasAuthorisations || this.requestNew;

        // ── Active / pending flow ─────────────────────────────────────────
        if (
            hasAuthorisations &&
            !this.requestNew &&
            (this.airspaceAuthorisationType === 'USA_LAANC' ||
                this.airspaceAuthorisationStatus === 'APPROVED' ||
                this.airspaceAuthorisationStatus === 'PENDING' ||
                this.airspaceAuthorisationStatus === 'PROVISIONAL' ||
                this.airspaceAuthorisationStatus === 'RESCINDED_AWAITING' ||
                this.airspaceAuthorisationStatus === 'DECLARED') &&
            !this.operationFailed &&
            !this.authorisationServiceUnavailable
        ) {
            return 'ACTIVE';
        }

        // ── Completed / closed ────────────────────────────────────────────
        if (
            hasAuthorisations &&
            this.airspaceAuthorisationType !== 'USA_LAANC' &&
            (this.airspaceAuthorisationStatus === 'COMPLETED' ||
                this.airspaceAuthorisationStatus === 'CLOSED') &&
            !this.operationFailed &&
            !this.authorisationServiceUnavailable &&
            !this.requestNew
        ) {
            return 'COMPLETED';
        }

        // ── Cancelled ─────────────────────────────────────────────────────
        if (
            hasAuthorisations &&
            this.airspaceAuthorisationType !== 'USA_LAANC' &&
            this.airspaceAuthorisationStatus === 'CANCELLED' &&
            !this.operationFailed &&
            !this.authorisationServiceUnavailable &&
            !this.requestNew
        ) {
            return 'CANCELLED';
        }

        // ── Rejected ──────────────────────────────────────────────────────
        if (
            hasAuthorisations &&
            this.airspaceAuthorisationType !== 'USA_LAANC' &&
            this.airspaceAuthorisationStatus === 'REJECTED' &&
            !this.requestNew
        ) {
            return 'REJECTED';
        }

        // ── Service error — suppress footer ───────────────────────────────
        if (this.operationFailed || this.authorisationServiceUnavailable) {
            return 'HIDDEN';
        }

        // ── New request ───────────────────────────────────────────────────
        if (
            isNewRequest &&
            this.authorisationForm != null &&
            this.authorisationPreview != null
        ) {
            return 'NEW_REQUEST';
        }

        // ── Form ready but no preview ──────────────────────────────────────
        if (
            this.authorisationForm != null &&
            this.authorisationPreview == null &&
            (!hasAuthorisations || this.requestNew)
        ) {
            return 'NO_PREVIEW';
        }

        return 'HIDDEN';
    }

    get formValid(): boolean {
        return this.authorisationForm?.valid ?? false;
    }

    get canSubmit(): boolean {
        return (
            this.formValid &&
            this.errorMessage == null &&
            !(
                this.authorisationForm?.value?.hasRemainingValidationErrors ??
                false
            ) &&
            this.additionalInformationFormValid
        );
    }
}
